import type { CSSProperties } from 'react';
import type { Colorways, PageJson } from '@apps/card/routes/CardCustomizer/components/PageJson.types';
import type { LayerData } from '@apps/card/routes/CardCustomizer/components/Layer.types';
import { absolutePhotoUrl, applyRenditionIfMediaServiceUrl } from './mediaService';
import type { Rendition } from './mediaService';

/**
 * Colorway live-preview helpers.
 *
 * A colorway template carries, on the DraftJson, a `colorways` object with an
 * `options` array of `{ id, values }` entries, each `values` an ordered
 * `string[]`. Layer color/src fields reference a `values` entry by index via a
 * `var(--N)` token; the color values make up the swatch. FE preview
 * surfaces (gallery tile, PDP, editor
 * canvas) render those tokens by setting the selected colorway's values as
 * inline CSS custom properties (`--N`) on an ancestor element —
 * non-destructive, so the draft keeps its tokens for the backend to collapse
 * at render time. See `useColorways`.
 */

const VAR_RE = /^var\(\s*--(\d+)\s*\)$/;

/**
 * Composes the theme-search embeddings-index row id for a colorway variant.
 * The index (themes.json/themes.bin) keys each colorway variant as
 * `${themeId}_${colorwayName}` (lowercase snake_case), e.g. `boldly_tomato`,
 * `emilio_hotels_blush`. Composition is deterministic even though themeIds
 * themselves contain underscores — we always join, never split.
 */
export function colorwayEmbeddingKey(themeId: string, colorwayId: string): string {
  return `${themeId}_${colorwayId.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
}

export type ColorwayIndexRow = Readonly<{
  /** Absent for a template with no colorways — the template is its own single row. */
  colorwayId?: string;
  /** Row id in the theme-search embeddings index (`themes.json`). */
  indexKey: string;
  /** Position in the template's catalog colorway order; `0` when there are none. */
  optionIndex: number;
}>;

/**
 * The embeddings-index rows that stand for a template: one per colorway, or a
 * single base row when it has none. Shared by every consumer that scores or
 * matches per colorway (gallery search, the `Colors` filter) so they resolve the
 * same row for the same colorway.
 *
 * The default colorway usually has no suffixed row — its colors live in the base
 * `themeId` row — so fall back to the base key when the suffix is absent
 * (`hasIndexRow`).
 */
export function colorwayIndexRows(themeId: string, colorways: Colorways | undefined, hasIndexRow: (key: string) => boolean): ColorwayIndexRow[] {
  const options = colorways?.options;
  if (!options?.length) return [{ indexKey: themeId, optionIndex: 0 }];
  const defaultId = colorways?.selectedId ?? options[0].id;
  return options.map((option, optionIndex) => {
    const suffixKey = colorwayEmbeddingKey(themeId, option.id);
    // TODO: Simplify this once `theme-search` is updated with all `themeId_colorwayId` combinations
    const indexKey = hasIndexRow(suffixKey) ? suffixKey : option.id === defaultId ? themeId : suffixKey;
    return { colorwayId: option.id, indexKey, optionIndex };
  });
}

/**
 * Colorway `values` mix colors and image references — image values are
 * media-service filenames while colors are always `rgb(...)`/`rgba(...)`. Only
 * the colors reach the swatch. See `swatchColorsByColorway`.
 */
const COLOR_VALUE_RE = /^rgba?\(/;

/**
 * Relative luminance (0 = black, 1 = white) of an `rgb()`/`rgba()` string,
 * or `undefined` if the channels can't be read. Uses the sRGB coefficients
 * rather than a plain average so yellows aren't mistaken for dark.
 */
export function luminance(color: string): number | undefined {
  const channels = color.match(/[\d.]+/g);
  if (!channels || channels.length < 3) return undefined;
  const [r, g, b] = channels.slice(0, 3).map(Number);
  if ([r, g, b].some(isNaN)) return undefined;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/**
 * Above this luminance a color is a candidate for dropping: white and ivory
 * card backgrounds (~0.97+) but also the pastel tints that pair with a
 * saturated color (pale pink ~0.92, pale sky blue ~0.93, pale green ~0.95).
 * Kept below those so the pastel cases clear it with margin.
 */
const PALE_LUMINANCE = 0.82;

/**
 * How much darker a sibling must be for a pale color to count as redundant.
 * Guards against dropping a color whose partners are barely darker — e.g. two
 * ivories, where neither is the "real" color of the pair.
 */
const PALE_CONTRAST_GAP = 0.25;

const isPale = (color: string) => {
  const value = luminance(color);
  return value !== undefined && value >= PALE_LUMINANCE;
};

/**
 * The color values of one colorway, keyed by their original index so the same
 * `var(--N)` slot lines up across colorways. Image references are omitted
 * rather than compacted away, which would shift later colors into the wrong
 * slot.
 */
function colorValuesByIndex(values: string[] | undefined): Map<number, string> {
  const result = new Map<number, string>();
  values?.forEach((value, index) => {
    if (COLOR_VALUE_RE.test(value)) result.set(index, value);
  });
  return result;
}

/** Every readable luminance across a set of colorways, ignoring unparseable values. */
function allLuminances(colorsByIndex: ReadonlyArray<Map<number, string>>): number[] {
  return colorsByIndex.flatMap(colors => [...colors.values()].map(luminance).filter((value): value is number => value !== undefined));
}

/**
 * Which `var(--N)` indices hold a pale color in *every* colorway that defines
 * them, and so can be dropped from all swatches.
 *
 * Pale-ness is judged per index rather than per colorway: an index whose colors
 * are pale everywhere is a shared background tint that distinguishes nothing,
 * while an index that is pale in some colorways and dark in others is exactly
 * what tells them apart. A white-on-black and black-on-white pair, for example,
 * has a pale color in each colorway but no uniformly pale index — so it keeps
 * both colors and the two swatches stay distinct.
 *
 * An index only qualifies if some colorway also has a substantially darker
 * color to stand in for the dropped tint.
 */
function redundantPaleIndices(colorsByIndex: ReadonlyArray<Map<number, string>>): Set<number> {
  const indices = new Set<number>();
  for (const colors of colorsByIndex) for (const index of colors.keys()) indices.add(index);
  // Nothing is worth dropping unless a genuinely darker color exists somewhere
  // in the set to carry the swatch — e.g. a set of ivories keeps its tints.
  // Measured across the whole set rather than per colorway, so one colorway
  // whose accent is only mid-toned cannot veto the reduction for the others.
  const darkest = Math.min(...allLuminances(colorsByIndex));
  if (!isFinite(darkest) || darkest > PALE_LUMINANCE - PALE_CONTRAST_GAP) return new Set();
  const redundant = new Set<number>();
  for (const index of indices) {
    const atIndex = colorsByIndex.map(colors => colors.get(index)).filter((color): color is string => color !== undefined);
    if (atIndex.length > 0 && atIndex.every(isPale)) redundant.add(index);
  }
  return redundant;
}

/**
 * Picks the swatch colors for every colorway in a set, as a `[colorwayId,
 * colors]` map.
 *
 * The swatch exists to tell colorways *apart*, not to reproduce the card, so it
 * may render darker than the card it stands for. Pale tints are dropped only
 * where they carry no distinguishing information, decided per `var(--N)` index
 * across the whole set:
 *
 * - `[pale-pink, red]`, `[pale-blue, blue]`, `[pale-green, green]` → index 0 is
 *   pale in every colorway, so it is dropped and each swatch shows just red,
 *   blue, green.
 * - add `[dark-grey, black]` and index 0 is no longer uniformly pale, so every
 *   color is shown.
 * - `[white, black]` and `[black, white]` keep both colors: each colorway has a
 *   pale color, but neither index is pale throughout, so dropping either would
 *   make the two swatches identical.
 *
 * A colorway with no color values maps to `['transparent']`.
 */
export function swatchColorsByColorway(options: ReadonlyArray<{ id: string; values: string[] }>): Map<string, string[]> {
  const colorsById = new Map(options.map(option => [option.id, colorValuesByIndex(option.values)]));
  const redundant = redundantPaleIndices([...colorsById.values()].filter(colors => colors.size > 0));
  const result = new Map<string, string[]>();
  for (const [id, colors] of colorsById) {
    if (colors.size === 0) {
      result.set(id, ['transparent']);
      continue;
    }
    const all = [...colors.entries()].sort(([a], [b]) => a - b);
    const kept = all.filter(([index]) => !redundant.has(index)).map(([, color]) => color);
    result.set(id, kept.length > 0 ? kept : all.map(([, color]) => color));
  }
  return result;
}

/**
 * Colorway arrays carry no `--image`/`--color` name prefix to key off, so we
 * determine which indices hold image references by scanning the page's layers:
 * `var(--N)` on an image's `src` marks index N as an image.
 */
export function imageVarIndices(layers: ReadonlyArray<LayerData>): Set<number> {
  const out = new Set<number>();
  for (const layer of layers) {
    const src = layer.type === 'image' ? layer.imageData.src : undefined;
    const match = src?.match(VAR_RE);
    if (match) out.add(Number(match[1]));
  }
  return out;
}

/** Builds inline CSS custom properties (`--N`) for the selected colorway's values. */
export function buildCssVarStyle(values: string[], imageIndices: Set<number>, rendition?: Rendition): CSSProperties {
  const result: Record<string, string> = {};
  values.forEach((value, index) => {
    if (imageIndices.has(index)) {
      const fullUrl = absolutePhotoUrl(value);
      result[`--${index}`] = `url(${applyRenditionIfMediaServiceUrl(fullUrl, rendition)})`;
    } else {
      result[`--${index}`] = value;
    }
  });
  return result as CSSProperties;
}

/**
 * Resolves a `var(--N)` layer field against a page's `cssVarStyle` — the colorway it is rendering
 * right now. Needed where the value leaves CSS (e.g. sent to the server), since only the browser
 * resolves the custom property.
 */
export function resolveCssVarRef(value: string | undefined, cssVarStyle: CSSProperties | undefined): string | undefined {
  const vars = cssVarStyle as Readonly<Record<string, string>> | undefined;
  return resolveVarRef(value, index => vars?.[`--${index}`]);
}

/**
 * The two color roles a card page plays, resolved to concrete `rgb(...)` values.
 *
 * A colorway's `values` array is unlabeled — nothing in the data says which slot
 * is the background and which is the ink. Rather than guess by position (index 1
 * is the ink on 469 layers in the catalog but an *image* on 73 others), the roles
 * are read off the page itself: whatever the page uses as its own `fill` is the
 * fill role, and whatever its text is printed in is the text role. Same trick as
 * `imageVarIndices`, which infers image slots from the layers that consume them.
 *
 * Either role is `undefined` when the page carries nothing to read it from — a
 * page with no text layers has no text role.
 */
export type CardColorRoles = Readonly<{
  fill?: string;
  text?: string;
}>;

/** The `values` of the colorway a page renders as, mirroring `useColorways`'s resolution order. */
function selectedColorwayValues(colorways: Colorways | undefined, colorwayId?: string): ReadonlyArray<string> {
  const options = colorways?.options;
  if (!options?.length) return [];
  const id = colorwayId ?? colorways?.selectedId;
  return (id ? options.find(option => option.id === id)?.values : undefined) ?? options[0].values;
}

/**
 * A layer field's concrete value: a `var(--N)` token resolved against the
 * colorway, anything else passed through. A page with no colorways therefore
 * resolves to its own literals, which is what makes the two roles readable on
 * every template rather than only the 153 that carry colorways.
 */
function resolveVarRef(value: string | undefined, valueAt: (index: number) => string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const match = value.match(VAR_RE);
  return match ? valueAt(Number(match[1])) : value;
}

/** The fields that carry a layer's ink color. Image, rectangle and foil layers have none. */
function inkFill(layer: LayerData): string | undefined {
  if (layer.type === 'text') return layer.textData.fill;
  if (layer.type === 'qrcode') return layer.qrcodeData.fill;
  return undefined;
}

/**
 * Of equally-common inks, the one that reads best on `background`.
 *
 * Only reached on a tie, which in the catalog means a design that sets its body
 * copy in one color and a decorative accent in another on the same number of
 * layers (`vintage_porcelain_foil`'s black body against its gold foil accent,
 * `magazine`'s white body against its pale pink display type). The body copy is
 * the one that contrasts with the card, so contrast picks it — and it is also
 * the safer of the two to reuse as the only ink on another surface.
 */
function mostLegibleAgainst(colors: ReadonlyArray<string>, background: string | undefined): string {
  const base = background === undefined ? undefined : luminance(background);
  if (base === undefined) return colors[0];
  let best = colors[0];
  let bestGap = -1;
  for (const color of colors) {
    const value = luminance(color);
    if (value === undefined) continue;
    const gap = Math.abs(value - base);
    if (gap > bestGap) {
      bestGap = gap;
      best = color;
    }
  }
  return best;
}

/**
 * Reads a page's fill and text colors for the colorway it renders as.
 *
 * The text role is the *most common* ink across the page's text and QR layers,
 * not the first: designs routinely set a display face in an accent color while
 * the body copy carries the card's real ink, and the body copy is what another
 * surface should borrow.
 *
 * Non-color values are dropped rather than returned — a slot holding a
 * media-service filename is an image, not a color anyone can paint with.
 */
export function resolveColorRoles(page: PageJson | undefined, colorwayId?: string): CardColorRoles {
  if (!page) return {};
  const values = selectedColorwayValues(page.colorways, colorwayId);
  const valueAt = (index: number) => values[index];
  const asColor = (value: string | undefined) => (value !== undefined && COLOR_VALUE_RE.test(value) ? value : undefined);

  const fill = asColor(resolveVarRef(page.fill, valueAt));

  const votes = new Map<string, number>();
  for (const layer of page.layers) {
    const ink = asColor(resolveVarRef(inkFill(layer), valueAt));
    if (ink !== undefined) votes.set(ink, (votes.get(ink) ?? 0) + 1);
  }
  if (votes.size === 0) return { fill };

  const most = Math.max(...votes.values());
  const contenders = [...votes.keys()].filter(color => votes.get(color) === most);
  return { fill, text: contenders.length === 1 ? contenders[0] : mostLegibleAgainst(contenders, fill) };
}
